import React from 'react'
import axios from 'axios'
import { MdDirectionsCar, MdRefresh } from 'react-icons/md'
import { getRequestHeaders } from '../../api'
import { baseUrl } from '../../assets/Animations'
import {
    buildDriveTimeRows,
    buildMissingDirectedPairs,
    buildSameInstrumentDriveTimeGroups,
    DRIVE_TIME_BUCKET,
    formatFamilyAddress,
    resolveFamilyPlaceKey,
} from './summerBreakDriveTimeUtils'
import './summer-break-drive-times.css'

const SummerBreakDriveTimes = ({ summary }) => {
    const groups = React.useMemo(
        () => buildSameInstrumentDriveTimeGroups(summary),
        [summary]
    )
    const [schoolResources, setSchoolResources] = React.useState({})
    const [loading, setLoading] = React.useState(false)
    const [loadError, setLoadError] = React.useState('')
    const [calculatingGroup, setCalculatingGroup] = React.useState('')
    const [groupErrors, setGroupErrors] = React.useState({})

    React.useEffect(() => {
        let cancelled = false
        const schoolIds = Array.from(new Set(groups.map((group) => group.schoolId)))

        if (schoolIds.length === 0) {
            setSchoolResources({})
            setLoading(false)
            setLoadError('')
            return undefined
        }

        setLoading(true)
        setLoadError('')
        Promise.all(schoolIds.map(async (schoolId) => {
            const [directoryResponse, edgeResponse] = await Promise.all([
                axios.get(`${baseUrl}/place-directory/${schoolId}`, getRequestHeaders()),
                axios.get(`${baseUrl}/drive-edges/${schoolId}`, getRequestHeaders()),
            ])

            return {
                schoolId,
                directories: directoryResponse?.data?.directories || [],
                edges: edgeResponse?.data?.edges || [],
            }
        }))
            .then((resources) => {
                if (cancelled) return
                setSchoolResources(Object.fromEntries(
                    resources.map((resource) => [resource.schoolId, resource])
                ))
            })
            .catch((error) => {
                if (cancelled) return
                console.error('Unable to load cached summer break drive times:', error)
                setLoadError('Cached drive times could not be loaded.')
            })
            .finally(() => {
                if (!cancelled) setLoading(false)
            })

        return () => {
            cancelled = true
        }
    }, [groups])

    if (groups.length === 0) {
        return (
            <section className='summer-break-drive-times'>
                <div className='summer-break-drive-times__heading'>
                    <MdDirectionsCar size={20} aria-hidden='true' />
                    <div>
                        <h3>Same-instrument drive times</h3>
                        <p>There are no same-instrument student pairs needing new teachers.</p>
                    </div>
                </div>
            </section>
        )
    }

    const calculateMissingTimes = async (group) => {
        setCalculatingGroup(group.key)
        setGroupErrors((previous) => ({ ...previous, [group.key]: '' }))

        try {
            let resources = schoolResources[group.schoolId] || { directories: [], edges: [] }
            const missingDirectories = new Map()

            group.students.forEach((student) => {
                if (resolveFamilyPlaceKey(student, resources.directories)) return

                const address = formatFamilyAddress(student.familyAddress)
                if (!student.familyId || !address) return
                missingDirectories.set(String(student.familyId), {
                    key: String(student.familyId),
                    address,
                    label: student.familyName || student.studentName || String(student.familyId),
                    roles: ['family'],
                    familyIds: [student.familyId],
                })
            })

            if (missingDirectories.size > 0) {
                await axios.post(
                    `${baseUrl}/place-directories/bulk`,
                    {
                        schoolId: group.schoolId,
                        directories: Array.from(missingDirectories.values()),
                    },
                    getRequestHeaders()
                )
                const directoryResponse = await axios.get(
                    `${baseUrl}/place-directory/${group.schoolId}`,
                    getRequestHeaders()
                )
                resources = {
                    ...resources,
                    directories: directoryResponse?.data?.directories || [],
                }
            }

            const rows = buildDriveTimeRows(
                group,
                resources.directories,
                resources.edges
            )
            const missingPairs = buildMissingDirectedPairs(rows)

            if (missingPairs.length === 0) {
                if (rows.some((row) => row.status === 'missing-location')) {
                    throw new Error('Add the missing family address before calculating this group.')
                }
                setSchoolResources((previous) => ({
                    ...previous,
                    [group.schoolId]: { ...resources, schoolId: group.schoolId },
                }))
                return
            }

            const edgeResponse = await axios.post(
                `${baseUrl}/drive-edges/fill`,
                {
                    schoolId: group.schoolId,
                    bucket: DRIVE_TIME_BUCKET,
                    todBucket: DRIVE_TIME_BUCKET,
                    pairs: missingPairs,
                },
                getRequestHeaders()
            )
            const newEdges = edgeResponse?.data?.edges || []
            setSchoolResources((previous) => ({
                ...previous,
                [group.schoolId]: {
                    schoolId: group.schoolId,
                    directories: resources.directories,
                    edges: [...resources.edges, ...newEdges],
                },
            }))
        } catch (error) {
            console.error('Unable to calculate summer break drive times:', error)
            setGroupErrors((previous) => ({
                ...previous,
                [group.key]: error?.message || 'Drive times could not be calculated.',
            }))
        } finally {
            setCalculatingGroup('')
        }
    }

    return (
        <section className='summer-break-drive-times'>
            <div className='summer-break-drive-times__heading'>
                <MdDirectionsCar size={20} aria-hidden='true' />
                <div>
                    <h3>Same-instrument drive times</h3>
                    <p>Students needing new teachers only · weekday 5 PM estimates</p>
                </div>
            </div>

            {loading && (
                <div className='summer-break-drive-times__state' role='status'>
                    Loading cached drive times…
                </div>
            )}
            {loadError && (
                <div className='summer-break-drive-times__state is-error' role='alert'>
                    {loadError}
                </div>
            )}

            {!loading && !loadError && (
                <div className='summer-break-drive-times__groups'>
                    {groups.map((group) => {
                        const resources = schoolResources[group.schoolId] || {
                            directories: [],
                            edges: [],
                        }
                        const rows = buildDriveTimeRows(
                            group,
                            resources.directories,
                            resources.edges
                        )
                        const missingRows = rows.filter((row) => (
                            row.status !== 'available'
                            || !Number.isFinite(row.fromToMinutes)
                            || !Number.isFinite(row.toFromMinutes)
                        ))

                        return (
                            <details className='summer-break-drive-times__group' key={group.key}>
                                <summary>
                                    <span>
                                        <strong>{group.schoolName}</strong>
                                        <span>{group.instrumentName}</span>
                                    </span>
                                    <span className='summer-break-drive-times__group-count'>
                                        {rows.length} {rows.length === 1 ? 'pair' : 'pairs'}
                                    </span>
                                </summary>
                                <div className='summer-break-drive-times__rows'>
                                    {rows.map((row) => (
                                        <div className={`summer-break-drive-times__row is-${row.status}`} key={row.key}>
                                            <span>
                                                {row.fromStudent.studentName}
                                                <span aria-hidden='true'>↔</span>
                                                {row.toStudent.studentName}
                                            </span>
                                            <strong>{row.displayTime}</strong>
                                        </div>
                                    ))}
                                </div>
                                {missingRows.length > 0 && (
                                    <button
                                        type='button'
                                        className='summer-break-drive-times__calculate'
                                        disabled={calculatingGroup === group.key}
                                        onClick={() => calculateMissingTimes(group)}
                                    >
                                        <MdRefresh size={15} aria-hidden='true' />
                                        {calculatingGroup === group.key
                                            ? 'Calculating…'
                                            : `Calculate ${missingRows.length} missing ${missingRows.length === 1 ? 'pair' : 'pairs'}`}
                                    </button>
                                )}
                                {groupErrors[group.key] && (
                                    <div className='summer-break-drive-times__group-error' role='alert'>
                                        {groupErrors[group.key]}
                                    </div>
                                )}
                            </details>
                        )
                    })}
                </div>
            )}
        </section>
    )
}

export default SummerBreakDriveTimes
